/**
 * `DECISION-STALE-ID` (P0): **the decision screen is drawn for the file the ADDRESS names, and posts to that file's id.**
 *
 * The address carries a file's REFERENCE (`decisionAddress`, `addresses.ts`), the door takes its id. On the first paint after a
 * route change the screen still held the PREVIOUS file's read and its id, until the entry effect replaced them: a click in
 * that window decided X while the address said Y, and the door cannot tell (a `DecisionBody` names no file). So the read is
 * drawn only when its reference IS the address's, and the id a decision or a preview goes to is taken from that matched file,
 * never from the `decisionId` state alone.
 */

/** The file the decision screen may draw: the read whose reference is the address's, otherwise nothing (the loading line). */
export function decisionFileOfAddress<File extends { readonly reference: string }>(
  file: File | null,
  addressReference: string | null,
): File | null {
  if (file === null || addressReference === null) return null;
  return file.reference === addressReference ? file : null;
}

/** Named, so a refusal reads as what it is in a console or a spec. */
export class DecisionTargetRefused extends Error {
  public readonly fileId: string;
  public readonly addressFileId: string | null;

  public constructor(fileId: string, addressFileId: string | null) {
    super(
      `DecisionTargetRefused: the file ${fileId} is not the file the address names (${addressFileId ?? "none"})`,
    );
    this.name = "DecisionTargetRefused";
    this.fileId = fileId;
    this.addressFileId = addressFileId;
  }
}

/** The id a decision (or its preview) may be posted to: the matched file's own, refused when it is not the address's file. */
export function decisionTargetId(file: { readonly id: string }, addressFileId: string | null): string {
  if (addressFileId === null || file.id !== addressFileId)
    throw new DecisionTargetRefused(file.id, addressFileId);
  return file.id;
}
